import { Check } from "lucide-react";
import { Mono, StepHead, Panel, SearchBox, GamePills, imgUrl, money } from "./ui";

export const TYPES = [
  { key: "card", label: "Carta suelta", hint: "Del catálogo" },
  { key: "sealed", label: "Sellado", hint: "Sobres, ETB, cajas" },
  { key: "lot", label: "Lote", hint: "Bulk, deck, colección" },
  { key: "accessory", label: "Accesorios", hint: "Fundas, carpetas, deck box · ", extra: "solo sellados" },
];
const EXAMPLES = ["charizard", "089/198", "151 mew"];

const TypeGrid = ({ type, onType }) => (
  <div className="grid grid-cols-2 gap-2">
    {TYPES.map((t) => (
      <button key={t.key} type="button" onClick={() => onType(t.key)} data-testid={`p1-type-${t.key}`}
        className="text-left p-3 rounded-[14px] border transition-colors"
        style={type === t.key ? { borderColor: "#FF4438", background: "rgba(255,68,56,.12)" } : { borderColor: "rgba(255,255,255,.1)", background: "#101010" }}>
        <div className="text-[15px] font-bold text-white">{t.label}</div>
        <div className="text-[11px] text-white/55 leading-snug">{t.hint}{t.extra && <span className="text-[#fbbf24] font-semibold">{t.extra}</span>}</div>
      </button>
    ))}
  </div>
);

const Recent = ({ rows, onAgain }) => (
  <div data-testid="p1-recent">
    <div className="flex items-center justify-between mb-2"><Mono>Publicar otra igual</Mono><span className="text-[11px] text-white/45">Mismos datos, 1 toque</span></div>
    <div className="space-y-2">
      {rows.map((p) => (
        <Panel key={p.id} className="flex items-center gap-3 p-2.5" data-testid={`p1-recent-${p.id}`}>
          <div className="w-8 h-11 rounded overflow-hidden bg-black shrink-0">{p.card?.image_url && <img src={imgUrl(p.card.image_url)} alt="" className="w-full h-full object-contain" />}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-white truncate">{p.card?.name}</div>
            <div className="sx-mono text-[9.5px] text-white/45 uppercase">{[p.language, p.condition, money(p.price)].filter(Boolean).join(" · ")}</div>
          </div>
          <button type="button" onClick={() => onAgain(p)} className="h-8 px-3 rounded-full border border-[#FF4438]/60 text-[#FF6B5E] text-xs font-bold" data-testid={`p1-again-${p.id}`}>+ Otra</button>
        </Panel>
      ))}
    </div>
  </div>
);

const Restored = ({ r, onView, onDiscard }) => {
  const at = r.at ? new Date(r.at) : null;
  const old = at && Date.now() - at.getTime() > 7 * 864e5;
  const date = at ? at.toLocaleDateString("es-EC", { day: "numeric", month: "short" }) : "antes";
  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] ${old ? "text-[#fbbf24]" : "text-white/65"}`} data-testid="p1-restored" data-old={old ? "true" : "false"}>
      <span>Tienes {r.n} carta{r.n === 1 ? "" : "s"} en la cola desde {date}</span>
      <span className="text-white/25">·</span>
      <button type="button" onClick={onView} className="font-bold text-white underline" data-testid="p1-restored-view">Ver cola</button>
      <span className="text-white/25">·</span>
      <button type="button" onClick={onDiscard} className="underline" data-testid="p1-restored-discard">Descartar</button>
    </div>
  );
};

// P1 · Inicio · un solo camino
export const P1Inicio = ({ games, game, onGame, type, onType, q, setQ, onScan, recent, onAgain, inReview, hasRequests, onRequests, payoutMissing, onPayout, restored, onViewQueue, onDiscardQueue }) => (
  <div className="space-y-4" data-testid="p1-inicio">
    <StepHead step={1} />
    <h1 className="font-extrabold tracking-[-.05em] leading-[.92] text-[38px] text-white">¿Qué vas<br />a <span className="italic font-normal text-white/60">vender?</span></h1>
    <TypeGrid type={type} onType={onType} />
    {restored && <Restored r={restored} onView={onViewQueue} onDiscard={onDiscardQueue} />}
    {payoutMissing && (
      <button type="button" onClick={onPayout} className="w-full text-left p-3 rounded-[14px] border border-[#FF4438]/60 bg-[#FF4438]/10 text-[13px] text-white flex items-center justify-between gap-3" data-testid="p1-payout-notice">
        <span>Agrega tus datos de cobro para poder publicar</span><span className="text-[#FF6B5E] font-bold shrink-0">Agregar</span>
      </button>
    )}
    <GamePills games={games} value={game} onChange={onGame} />
    <SearchBox value={q} onChange={setQ} onScan={onScan} testid="p1-search" />
    <div className="flex flex-wrap items-center gap-1.5">
      {EXAMPLES.map((e) => <button key={e} type="button" onClick={() => setQ(e)} className="h-7 px-2.5 rounded-full border border-white/10 sx-mono text-[10px] text-white/70" data-testid={`p1-example-${e.replace(/\W/g, "")}`}>{e}</button>)}
    </div>
    <div className="text-[11px] text-white/40 -mt-2">← así también funciona</div>
    <div className="flex gap-3 p-3.5 rounded-[14px] border border-emerald-400/40 bg-emerald-400/5" data-testid="p1-notice">
      <span className="w-6 h-6 rounded-md bg-emerald-400 grid place-items-center shrink-0"><Check size={15} className="text-black" strokeWidth={3} /></span>
      <p className="text-[13px] text-white/80 leading-snug"><b className="text-white">No tienes que registrar nada.</b> Si la carta está en el catálogo, la eliges y se publica al instante.</p>
    </div>
    {recent.length > 0 && <Recent rows={recent} onAgain={onAgain} />}
    {hasRequests && (
      <button type="button" onClick={onRequests} className="w-full flex items-center justify-between gap-3 p-3 rounded-[14px] border border-[#fbbf24]/40 bg-[#fbbf24]/5" data-testid="p1-requests">
        <span className="flex items-center gap-2 min-w-0"><Mono tone="#fbbf24">Tus solicitudes</Mono><span className="text-[12px] text-white/75 truncate">{inReview} en revisión · se publica sola</span></span>
        <span className="text-[13px] font-bold text-white shrink-0">Ver →</span>
      </button>
    )}
  </div>
);
